import { Input, InputProps } from "@headlessui/react";
import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { cn } from "../../utils/classes";
import { IconButtonV2 } from "../button";
import { Icon, IconType, isIconType } from "../icon";
import styles from "./input.module.css";

export type InputSize = "lg" | "md" | "sm" | "xs";

// TODO: Use enums to make this more maintainable, or use nested classes
const getClearButtonInputSize = (size: InputSize) => {
  switch (size) {
    case "lg":
      return "md";
    case "md":
      return "sm";
    case "sm":
    case "xs":
    default:
      return "xs";
  }
};

export interface InputV2Props<V extends string | number | string[]>
  extends Omit<InputProps, "size" | "onChange" | "value" | "defaultValue"> {
  iconLeft?: IconType;
  iconLeftClassName?: string;
  containerClassName?: string;
  size?: InputSize;
  value: V;
  defaultValue?: V;
  onChange?: (value: V | undefined) => void;
}

const InputIcon = ({
  icon,
  size,
  className,
}: {
  icon: IconType;
  size: InputSize;
  className: string;
}) => {
  return isIconType(icon) ? (
    <Icon
      aria-hidden="true"
      type={icon}
      className={cn("absolute size-4", className)}
    />
  ) : (
    <div className={cn("absolute size-4", className)}>{icon}</div>
  );
};

export const InputV2 = forwardRef(function InputV2<
  V extends string | number | string[],
>(props: InputV2Props<V>, ref: ForwardedRef<HTMLInputElement>) {
  const {
    iconLeft,
    iconLeftClassName = "",
    containerClassName,
    value: defaultValue,
    size = "sm",
    onChange,
    ...inputProps
  } = props;

  const [value, setValue] = useState(defaultValue);

  useEffect(() => onChange?.(value), [value]);

  return (
    <div
      className={cn(
        "relative flex items-center",
        styles.container,
        containerClassName,
      )}
    >
      {iconLeft && (
        <InputIcon
          icon={iconLeft}
          size={size}
          className={cn(
            size === "lg" ? "left-4" : "left-2",
            value ? "text-gray-900" : "text-gray-400",
            iconLeftClassName,
          )}
        />
      )}
      <Input
        ref={ref}
        {...inputProps}
        value={value}
        onChange={(e) => setValue(e.target.value as V)}
        className={cn(
          "flex-1 overflow-hidden border border-gray-300 bg-white px-4 font-medium text-gray-900 placeholder:text-gray-500 hover:bg-gray-300 disabled:bg-gray-300",
          styles.input,
          { [styles.with_clear]: typeof value !== "undefined" && value !== "" },
          "transition-colors duration-300 ease-in",
          size === "xs" && "h-6 rounded-xxs text-xs",
          size === "sm" && "h-8 rounded-xxs text-sm",
          size === "md" && "h-10 rounded-xs text-sm",
          size === "lg" && "h-12 rounded-xs text-sm",
          iconLeft && (size === "lg" ? "pl-10" : "pl-8"),
          props.className,
        )}
      />
      {typeof value !== "undefined" && value !== "" && (
        <IconButtonV2
          // onClick={}
          icon={"X"}
          size={getClearButtonInputSize(size)}
          onClick={() => setValue("" as V)}
          className={cn(value ? "text-gray-900" : "text-gray-400")}
        />
      )}
    </div>
  );
}) as <V extends string | number | string[]>(
  props: InputV2Props<V> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReactElement;
